import { useEffect, useRef } from 'react';
import { Formik, Form, useFormikContext } from 'formik';
import { Autocomplete } from '@react-google-maps/api';
import { useMapsApiLoader } from '@/hooks/useMapsApiLoader';
import type { PropertyRequest } from '@/types/property.types';

import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { FormError } from '@/components/ui/form-field';
import { SimpleSelect } from '@/components/ui/simple-select';
import { Button } from '@/components/ui/button';
import { Typography } from '@/components/ui/typography';
import {
  dropDownURLS,
  extractMapFormData,
} from '@/components/properties/add-property/hooks/propertyFormUtils';
import {
  buildAddressValidationSchema,
  type AddressFormValues,
} from '@/components/properties/add-property/addressValidationSchema';
import { useDropdownOptions } from '@/components/properties/services/useDropdownOptions';
import { getEntityDropdownConfig } from '@/services/entity-tree';
import { orderStateOptions } from '@/config/options.config';

export interface NewPropertySubmitValues extends AddressFormValues {
  propertyId: string;
  latitude: string;
  longitude: string;
}

// Canonical mapping from the shared property form's submit values to the
// create-property API request. Colocated with NewPropertySubmitValues so
// both consumers (orders AddNewPropertyView, properties NewPropertyDrawer)
// map identically — no drift when PropertyRequest changes.
export function buildPropertyRequest(v: NewPropertySubmitValues): PropertyRequest {
  return {
    propertyType: v.PropertyType,
    address1: v.address,
    unitNumber: v.other || null,
    clientPropertyId: v.propertyId || null,
    city: v.city,
    state: v.state,
    zipCode: v.zipCode,
    entity: v.entity || null,
    entityUUID: v.entityUUID || null,
    latitude: v.latitude || null,
    longitude: v.longitude || null,
    isActive: true,
  };
}

interface NewPropertyFormProps {
  onSubmit: (values: NewPropertySubmitValues) => void | Promise<void>;
  onCancel: () => void;
  isSubmitting: boolean;
  /** Reports Formik dirty changes up to the parent so the drawer can
   *  prompt before discarding an in-progress edit. */
  onDirtyChange?: (dirty: boolean) => void;
}

const INITIAL_VALUES: NewPropertySubmitValues = {
  PropertyType: '',
  propertyId: '',
  address: '',
  other: '',
  city: '',
  state: '',
  zipCode: '',
  entity: '',
  entityUUID: '',
  latitude: '',
  longitude: '',
};

function FormikDirtyWatcher({
  onDirtyChange,
}: {
  onDirtyChange?: (dirty: boolean) => void;
}) {
  const { dirty } = useFormikContext();
  useEffect(() => {
    onDirtyChange?.(dirty);
  }, [dirty, onDirtyChange]);
  return null;
}

export function NewPropertyForm({
  onSubmit,
  // Parent renders the visible Cancel button; we accept the prop for
  // completeness so callers can wire it without a TS error. The hidden
  // submit button at the bottom of <Form> is triggered via a parent ref.
  onCancel: _onCancel,
  isSubmitting,
  onDirtyChange,
}: NewPropertyFormProps) {
  const entityDropdownConfig = getEntityDropdownConfig();
  const { isLoaded } = useMapsApiLoader();
  const propertyTypeOptions = useDropdownOptions(
    dropDownURLS.propertyTypeURL,
    'property',
  );

  // Seed the entity field for the single-entity case so submission
  // succeeds without showing the field.
  const initialValues: NewPropertySubmitValues = {
    ...INITIAL_VALUES,
    entity: entityDropdownConfig.isDisabled
      ? entityDropdownConfig.defaultEntityName
      : '',
    entityUUID: entityDropdownConfig.isDisabled
      ? entityDropdownConfig.defaultEntityUUID ?? ''
      : '',
  };

  const showEntityField = !entityDropdownConfig.isDisabled;
  const validationSchema = buildAddressValidationSchema({
    isRequired: showEntityField,
  });

  const autocompleteRef = useRef<google.maps.places.Autocomplete | null>(null);

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={onSubmit}
    >
      {({ values, errors, touched, handleChange, setFieldValue, setValues }) => {
        const handlePlaceChanged = () => {
          if (!autocompleteRef.current) return;
          const place = autocompleteRef.current.getPlace();
          const { mapValue } = extractMapFormData(place);
          setValues({
            ...values,
            address: mapValue.address,
            city: mapValue.city,
            state: mapValue.state,
            zipCode: mapValue.zipCode,
            latitude: String(mapValue.lat),
            longitude: String(mapValue.lng),
          });
        };

        return (
          <Form className="space-y-6" data-testid="new-property-form">
            <FormikDirtyWatcher onDirtyChange={onDirtyChange} />

            <Typography className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              Property details
            </Typography>

            {showEntityField && (
              <SimpleSelect
                options={entityDropdownConfig.options}
                name="entity"
                controlId="entity"
                label="Entity"
                isRequired
                value={
                  values.entityUUID
                    ? entityDropdownConfig.options.find(
                        (o) => o.value === values.entityUUID
                      ) || null
                    : null
                }
                placeholder="Please Select an Entity"
                errors={touched.entity ? (errors.entity as string) : undefined}
                setFieldValue={(field: string, value: string) => {
                  const opt = entityDropdownConfig.options.find(
                    (o) => o.value === value
                  );
                  setFieldValue(field, opt?.label ?? '');
                  setFieldValue('entityUUID', value);
                }}
                field="entity"
              />
            )}

            <SimpleSelect
              options={propertyTypeOptions}
              name="PropertyType"
              controlId="PropertyType"
              label="Property Type"
              isRequired
              value={
                values.PropertyType
                  ? propertyTypeOptions.find(
                      (o) => o.value === values.PropertyType,
                    ) || null
                  : null
              }
              placeholder="Select…"
              errors={
                touched.PropertyType
                  ? (errors.PropertyType as string)
                  : undefined
              }
              setFieldValue={setFieldValue}
              field="PropertyType"
            />

            <div>
              <Label htmlFor="address" required>
                Street Address
              </Label>
              {isLoaded ? (
                <Autocomplete
                  onLoad={(ac) => {
                    autocompleteRef.current = ac;
                  }}
                  onPlaceChanged={handlePlaceChanged}
                >
                  <Input
                    id="address"
                    name="address"
                    type="text"
                    inputMode="text"
                    autoComplete="street-address"
                    data-1p-ignore="true"
                    data-lpignore="true"
                    placeholder="123 Main Street"
                    value={values.address}
                    onChange={handleChange}
                    className={
                      touched.address && errors.address
                        ? 'border-destructive'
                        : ''
                    }
                  />
                </Autocomplete>
              ) : (
                <Input
                  id="address"
                  name="address"
                  type="text"
                  inputMode="text"
                  autoComplete="street-address"
                  data-1p-ignore="true"
                  data-lpignore="true"
                  placeholder="123 Main Street"
                  value={values.address}
                  onChange={handleChange}
                />
              )}
              <FormError
                message={touched.address ? (errors.address as string) : undefined}
              />
            </div>

            <div>
              <Label htmlFor="other">Apt / Suite / Other (Optional)</Label>
              <Input
                id="other"
                name="other"
                type="text"
                autoComplete="address-line2"
                placeholder="e.g. Apt 4B, Suite 200, Unit C"
                value={values.other}
                onChange={handleChange}
              />
            </div>

            <div>
              <Label htmlFor="city" required>
                City
              </Label>
              <Input
                id="city"
                name="city"
                type="text"
                autoComplete="address-level2"
                value={values.city}
                onChange={handleChange}
                className={
                  touched.city && errors.city ? 'border-destructive' : ''
                }
              />
              <FormError
                message={touched.city ? (errors.city as string) : undefined}
              />
            </div>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <SimpleSelect
                options={orderStateOptions}
                name="state"
                controlId="state"
                label="State"
                isRequired
                value={
                  values.state
                    ? orderStateOptions.find((o) => o.value === values.state) ||
                      null
                    : null
                }
                placeholder="Select…"
                errors={touched.state ? (errors.state as string) : undefined}
                setFieldValue={setFieldValue}
                field="state"
              />
              <div>
                <Label htmlFor="zipCode" required>
                  ZIP
                </Label>
                <Input
                  id="zipCode"
                  name="zipCode"
                  type="text"
                  inputMode="numeric"
                  autoComplete="postal-code"
                  maxLength={5}
                  placeholder="00000"
                  value={values.zipCode}
                  onChange={handleChange}
                  className={
                    touched.zipCode && errors.zipCode
                      ? 'border-destructive'
                      : ''
                  }
                />
                <FormError
                  message={
                    touched.zipCode ? (errors.zipCode as string) : undefined
                  }
                />
              </div>
            </div>

            <div>
              <Label htmlFor="propertyId">Property ID (Optional)</Label>
              <Input
                id="propertyId"
                name="propertyId"
                type="text"
                autoComplete="off"
                placeholder="e.g. UNIT-1042"
                value={values.propertyId}
                onChange={handleChange}
              />
            </div>

            <Button
              type="submit"
              disabled={isSubmitting}
              className="hidden"
              data-testid="hidden-submit"
            >
              Submit
            </Button>
          </Form>
        );
      }}
    </Formik>
  );
}
